import React, { useState } from 'react';
import VoiceSettingsPanel from '../components/VoiceSettingsPanel';

const ScriptBuilder = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [scriptName, setScriptName] = useState('');
  const [script, setScript] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const TEMPLATES = [
    {
      name: 'Sales Qualification (BANT)',
      content:
        `You are a polite inside-sales agent qualifying inbound leads using BANT.\n\nObjectives:\n- Identify Budget, Authority, Need, and Timeline\n- Keep responses concise and ask one focused question at a time\n- Confirm summary before closing\n\nOpening:\n"Hi, this is {{agent_name}} with {{company}}. Thanks for your interest! May I ask a few quick questions to confirm fit?"\n\nFlow:\n1) Need: Ask about their core problem and current workaround\n2) Authority: Confirm decision maker(s) and stakeholders\n3) Budget: Explore available budget range or approval path\n4) Timeline: Determine urgency and key dates\n\nClose:\n- Summarize BANT findings in a single paragraph\n- Offer the next step (demo, trial, custom quote)\n- Confirm the best contact method and time\n\nTone: friendly, confident, never pushy.`,
    },
    {
      name: 'Appointment Scheduling',
      content:
        `You are an appointment scheduling assistant for a professional service.\n\nGoals:\n- Offer available slots\n- Confirm date, time, timezone, and contact details\n- Handle reschedules/cancellations gracefully\n\nProcedure:\n1) Gather service type and duration if relevant\n2) Offer 3 time slots and accept a custom preference\n3) Confirm: name, phone/email, location/tele-visit\n4) Send a concise confirmation summary\n\nConstraints:\n- Avoid medical advice or policy commitments\n- Keep messages under 2 sentences unless summarizing details\n\nTone: warm, concise, helpful.`,
    },
    {
      name: 'Customer Support Triage',
      content:
        `You are a first‑line support triage agent.\n\nTasks:\n- Empathize and clarify the issue\n- Collect necessary details (device, OS, version, steps tried)\n- Determine severity and category\n- Provide immediate tips if safe, else escalate with a clean summary\n\nEscalation Summary Template:\nIssue: <one sentence>\nEnvironment: <device/OS/version>\nRepro Steps: <numbered list>\nSeverity: <low/medium/high>\nCustomer Impact: <brief>\n\nTone: empathetic, solution‑oriented, realistic.`,
    },
    {
      name: 'Collections & Billing FAQ',
      content:
        `You are a billing FAQ and collections assistant.\n\nScope:\n- Answer common billing/payment questions\n- Offer secure payment links when appropriate\n- Never request full card numbers or sensitive PII\n- Escalate complex disputes to billing specialists\n\nFlow:\n1) Verify identity with non‑sensitive details\n2) Understand the issue (invoice, charge, refund)\n3) Provide policy‑consistent guidance\n4) Offer next‑step and summarize resolution\n\nTone: calm, professional, reassuring.`,
    },
  ];

  // New customization fields
  const [maxWords, setMaxWords] = useState(30);
  const [avoidTopics, setAvoidTopics] = useState('');
  const [toneStyle, setToneStyle] = useState('professional medical scheduler');
  const [responseDensity, setResponseDensity] = useState('balanced');
  const [empathyLevel, setEmpathyLevel] = useState('medium');
  const [formatting, setFormatting] = useState('');

  // Voice and Language settings
  const [language, setLanguage] = useState('en');
  const [voiceId, setVoiceId] = useState('');
  const [voices, setVoices] = useState([]);
  const [languages, setLanguages] = useState({});
  const [loadingVoices, setLoadingVoices] = useState(false);

  // Advanced Voice Settings (11Labs customization)
  const [voiceSettings, setVoiceSettings] = useState({
    stability: 0.75,
    similarityBoost: 0.9,
    speakerBoost: true
  });

  // AI Model selection
  const [aiModel, setAiModel] = useState('gpt-4o-mini');
  const [temperature, setTemperature] = useState(0.5);
  const [maxTokens, setMaxTokens] = useState(500);

  // Human Handoff settings
  const [enableHumanHandoff, setEnableHumanHandoff] = useState(false);
  const [inboundHumanNumber, setInboundHumanNumber] = useState('');
  const [ringTimeoutSeconds, setRingTimeoutSeconds] = useState(10);
  const [greetingMessage, setGreetingMessage] = useState('');

  // Load voices and languages on mount
  React.useEffect(() => {
    const loadVoices = async () => {
      try {
        setLoadingVoices(true);
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
        const res = await fetch(`${apiBase}/api/voices`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
        });
        const data = await res.json();
        if (data.success) {
          const voicesList = data.voices || [];
          console.log('[Script] Voices loaded from API:', voicesList.map(v => ({ name: v.name, id: v.id })));
          const miranda = voicesList.find(v => v.name && v.name.toLowerCase().includes('miranda'));
          if (miranda) console.log('[Script] Miranda voice found:', miranda);
          setVoices(voicesList);
          setLanguages(data.languages || {});
          // Set first English voice as default if available
          const englishVoice = voicesList.find(v => v.language === 'en' || v.language === 'en-US');
          if (englishVoice) {
            setVoiceId(englishVoice.id);
          }
        }
      } catch (err) {
        console.error('Error loading voices:', err);
      } finally {
        setLoadingVoices(false);
      }
    };
    loadVoices();
  }, []);

  // Filter voices by selected language (Cartesia voices are multilingual)
  const voicesForLanguage = voices.filter((v) => {
    if (v.provider === 'Cartesia' || v.multilingual) return true;
    if (language === 'en') return v.language === 'en' || v.language === 'en-US' || v.language === 'en-GB' || v.provider === 'Kokoro';
    return v.language === language || v.language === `${language}-US` || v.language === `${language}-GB`;
  });

  const handleGenerateScript = async () => {
    setLoading(true);
    setScript('');

    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const res = await fetch(`${apiBase}/api/generate-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ firstName, lastName, purpose, businessName }),
      });

      const data = await res.json();

      if (res.ok) {
        setScript(data.script);
      } else {
        setScript(`❌ Error: ${data.message || 'Unable to generate script.'}`);
      }
    } catch (err) {
      setScript(`❌ Request failed.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveScript = async () => {
    if (!scriptName.trim()) return alert('Please provide a script name.');
    if (!script.trim()) return alert('No script to save.');

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      // Call guideline generator
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const guidelineRes = await fetch(`${apiBase}/api/generate-guidelines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          maxWords,
          avoidTopics: avoidTopics.split(',').map(t => t.trim()).filter(Boolean),
          toneStyle,
          responseDensity,
          empathyLevel,
          formatting: formatting.split(',').map(f => f.trim()).filter(Boolean),
        }),
      });

      const guidelineData = await guidelineRes.json();

      const fullScript = `${script.trim()}\n\n### GUIDELINES ###\n${(guidelineData.rules || []).join('\n')}`;

      const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } })();
      const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId || null;

      console.log('[Script] Saving script with:', { scriptName, voiceId, voiceSettings, language });
      const saveRes = await fetch(`${apiBase}/api/save-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          scriptName,
          script: fullScript,
          firstName,
          lastName,
          businessName,
          ownerId,
          // AI Model settings
          aiModel,
          temperature,
          maxTokens,
          // Voice and language settings
          language,
          voiceId,
          voiceSettings,  // Advanced 11Labs customization
          // Prompt/formatting preferences
          toneStyle,
          empathyLevel,
          responseDensity,
          maxWords,
          formatting: formatting.split(',').map(f => f.trim()).filter(Boolean),
          avoidTopics: avoidTopics.split(',').map(t => t.trim()).filter(Boolean),
          // Human Handoff settings for text prompt scripts
          ...(enableHumanHandoff && {
            humanHandoff: {
              enabled: true,
              inboundHumanNumber,
              ringTimeoutSeconds,
              greetingMessage,
            },
          }),
        }),
      });

      const data = await saveRes.json();

      if (saveRes.ok) {
        alert('✅ Script saved successfully!');
      } else {
        alert(`❌ Error saving script: ${data.message || 'Unknown error.'}`);
      }
    } catch (err) {
      alert('❌ Failed to save script.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 md:gap-6 p-4 md:p-6">
      {/* Left Panel */}
      <div className="flex-1 border border-gray-200 rounded-xl shadow p-4 md:p-6">
        <h2 className="text-xl font-semibold mb-4">Create AI Agent Script</h2>

        {/* Form Inputs */}
        <div className="space-y-4">
          <input className="w-full border rounded px-3 py-2" placeholder="Client First Name" value={firstName} onChange={e => setFirstName(e.target.value)} />
          <input className="w-full border rounded px-3 py-2" placeholder="Client Last Name" value={lastName} onChange={e => setLastName(e.target.value)} />
          <input className="w-full border rounded px-3 py-2" placeholder="Business Name" value={businessName} onChange={e => setBusinessName(e.target.value)} />
          <textarea className="w-full border rounded px-3 py-2" rows={3} placeholder="Purpose of the AI Agent" value={purpose} onChange={e => setPurpose(e.target.value)} />

          {/* Guideline Customization */}
          <hr />
          <label className="block font-medium">Max Words Per Response: {maxWords}</label>
          <input type="range" min={5} max={50} value={maxWords} onChange={e => setMaxWords(parseInt(e.target.value))} />

          <label className="block font-medium">Avoid Topics (comma-separated)</label>
          <textarea className="w-full border rounded px-3 py-2" rows={2} value={avoidTopics} onChange={e => setAvoidTopics(e.target.value)} />

          <label className="block font-medium">Tone Style</label>
          <select className="w-full border rounded px-3 py-2" value={toneStyle} onChange={e => setToneStyle(e.target.value)}>
            <option value="friendly and casual">Friendly and casual</option>
            <option value="formal customer service">Formal customer service</option>
            <option value="warm and conversational">Warm and conversational</option>
            <option value="gentle and reassuring">Gentle and reassuring</option>
            <option value="confident sales agent">Confident sales agent</option>
            <option value="neutral and informative">Neutral and informative</option>
            <option value="nurturing nurse assistant">Nurturing nurse assistant</option>
            <option value="laid-back and humorous">Laid-back and humorous</option>
            <option value="instructional teacher">Instructional teacher</option>
          </select>

          <label className="block font-medium">Response Density</label>
          <select className="w-full border rounded px-3 py-2" value={responseDensity} onChange={e => setResponseDensity(e.target.value)}>
            <option value="brief">Brief</option>
            <option value="balanced">Balanced</option>
            <option value="detailed">Detailed</option>
          </select>

          <label className="block font-medium">Empathy Level</label>
          <select className="w-full border rounded px-3 py-2" value={empathyLevel} onChange={e => setEmpathyLevel(e.target.value)}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>

          <label className="block font-medium">Formatting Preferences (comma-separated)</label>
          <textarea className="w-full border rounded px-3 py-2" rows={2} value={formatting} onChange={e => setFormatting(e.target.value)} />

          {/* Voice & Language Section */}
          <hr className="my-4" />
          <div className="space-y-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-900">🎙️ Voice & Language Settings</h3>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-2">
                Language *
              </label>
              <select
                className="w-full border border-purple-300 rounded px-3 py-2 text-sm"
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value);
                  // Reset voice when language changes
                  const voicesForNewLang = voices.filter(v =>
                    v.language === e.target.value ||
                    v.language === `${e.target.value}-US` ||
                    v.language === `${e.target.value}-GB`
                  );
                  if (voicesForNewLang.length > 0) {
                    setVoiceId(voicesForNewLang[0].id);
                  }
                }}
              >
                {Object.entries(languages).map(([code, langInfo]) => (
                  <option key={code} value={code}>
                    {langInfo.name} ({code.toUpperCase()}){langInfo.region ? ` - ${langInfo.region}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-purple-600 mt-1">Select the output language for voice responses</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-700 mb-2">
                Voice *
              </label>
              {loadingVoices ? (
                <div className="text-sm text-purple-600">Loading voices...</div>
              ) : voicesForLanguage.length > 0 ? (
                <div className="space-y-2">
                  <select
                    className="w-full border border-purple-300 rounded px-3 py-2 text-sm font-medium"
                    value={voiceId}
                    onChange={(e) => setVoiceId(e.target.value)}
                  >
                    <option value="">-- Select a voice --</option>
                    {voicesForLanguage.map(voice => (
                      <option key={voice.id} value={voice.id}>
                        {voice.name} {voice.language ? `(${voice.language})` : voice.category ? `(${voice.category})` : ''}
                      </option>
                    ))}
                  </select>
                  {voiceId && voicesForLanguage.find(v => v.id === voiceId) && (
                    <div className="p-2 bg-purple-50 rounded border border-purple-200">
                      <p className="text-xs font-semibold text-purple-900">Selected Voice ID:</p>
                      <p className="text-xs text-purple-700 font-mono break-all">{voiceId}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-purple-600 p-2 bg-white rounded border border-purple-200">
                  No voices available for {languages[language]?.name || language}
                </div>
              )}
              <p className="text-xs text-purple-600 mt-1">The AI will speak using this voice</p>
            </div>

            {voicesForLanguage.find(v => v.id === voiceId)?.preview_url && (
              <div className="mt-3 p-2 bg-white rounded border border-purple-200">
                <p className="text-xs font-medium text-purple-700 mb-2">Voice Preview:</p>
                <audio
                  controls
                  className="w-full h-6"
                  src={voicesForLanguage.find(v => v.id === voiceId)?.preview_url}
                />
              </div>
            )}

            <div className="text-xs text-purple-600 bg-white p-2 rounded">
              <p className="font-medium mb-1">📝 How it works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>System prompts stay in <strong>English</strong></li>
                <li>User input is transcribed and understood in <strong>{languages[language]?.name}</strong></li>
                <li>AI responses are translated to <strong>{languages[language]?.name}</strong></li>
                <li>Output is spoken in the selected voice and language</li>
              </ul>
            </div>

            {/* Advanced Voice Settings */}
            <VoiceSettingsPanel
              voiceSettings={voiceSettings}
              onChange={setVoiceSettings}
              onPreview={() => {
                if (voicesForLanguage.find(v => v.id === voiceId)?.preview_url) {
                  const audio = new Audio(voicesForLanguage.find(v => v.id === voiceId).preview_url);
                  audio.play();
                }
              }}
            />
          </div>

          {/* AI Model Selection Section */}
          <hr className="my-4" />
          <div className="space-y-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-indigo-900">🤖 AI Model Selection</h3>

            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-2">
                LLM Model *
              </label>
              <select
                className="w-full border border-indigo-300 rounded px-3 py-2 text-sm"
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
              >
                <option value="gpt-4o-mini">OpenAI - GPT-4o Mini (Fast, Efficient)</option>
                <option value="gpt-4o">OpenAI - GPT-4o (Advanced Reasoning)</option>
                <option value="claude-3-5-sonnet">Anthropic - Claude 3.5 Sonnet (Balanced)</option>
                <option value="claude-3-opus">Anthropic - Claude 3 Opus (Most Capable)</option>
                <option value="llama-3-70b">Meta - Llama 3 70B (Open Source)</option>
                <option value="llama-2-70b">Meta - Llama 2 70B (Open Source)</option>
              </select>
              <p className="text-xs text-indigo-600 mt-1">Choose the AI model to power your agent</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-2">
                Temperature: <span className="font-bold">{temperature.toFixed(2)}</span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
                className="w-full h-2 bg-indigo-300 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-indigo-600 mt-1">
                <span>Focused (0.0)</span>
                <span>Balanced (0.5)</span>
                <span>Creative (1.0)</span>
              </div>
              <p className="text-xs text-indigo-600 mt-2">
                Lower values = more consistent, predictable responses. Higher values = more creative, varied responses.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-indigo-700 mb-2">
                Max Tokens: <span className="font-bold">{maxTokens}</span>
              </label>
              <input
                type="range"
                min="50"
                max="4000"
                step="50"
                value={maxTokens}
                onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                className="w-full h-2 bg-indigo-300 rounded-lg appearance-none cursor-pointer"
              />
              <p className="text-xs text-indigo-600 mt-1">Maximum length of AI responses (1 token ≈ 4 characters)</p>
            </div>

            <div className="text-xs text-indigo-600 bg-white p-2 rounded">
              <p className="font-medium mb-1">💡 Model Guide:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>GPT-4o:</strong> Best for complex reasoning and accuracy</li>
                <li><strong>Claude 3.5 Sonnet:</strong> Excellent balance of capability and cost</li>
                <li><strong>Llama 3:</strong> Open-source, good for cost-conscious deployments</li>
              </ul>
            </div>
          </div>

          {/* Human Handoff Section */}
          <hr className="my-4" />
          <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <label className="flex items-center gap-3 font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={enableHumanHandoff}
                onChange={(e) => setEnableHumanHandoff(e.target.checked)}
                className="w-5 h-5 rounded"
              />
              <span>📞 Enable Human Handoff (Inbound Calls)</span>
            </label>

            {enableHumanHandoff && (
              <div className="space-y-3 ml-8 mt-2">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Inbound Human Phone Number *
                  </label>
                  <input
                    type="tel"
                    className="w-full border border-blue-300 rounded px-3 py-2 text-sm"
                    placeholder="+1-555-0123 or +1555012"
                    value={inboundHumanNumber}
                    onChange={(e) => setInboundHumanNumber(e.target.value)}
                  />
                  <p className="text-xs text-blue-600 mt-1">E.164 format (e.g., +1-555-0123)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-2">
                    Ring Timeout: <span className="font-bold">{ringTimeoutSeconds}s</span>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={ringTimeoutSeconds}
                    onChange={(e) => setRingTimeoutSeconds(parseInt(e.target.value))}
                    className="w-full h-2 bg-blue-300 rounded-lg appearance-none cursor-pointer"
                  />
                  <p className="text-xs text-blue-600 mt-1">How long to ring before falling back to AI (1-30 seconds)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Greeting Message (Optional)
                  </label>
                  <textarea
                    className="w-full border border-blue-300 rounded px-3 py-2 text-sm resize-none"
                    rows={2}
                    placeholder="e.g., Connecting you to an agent..."
                    value={greetingMessage}
                    onChange={(e) => setGreetingMessage(e.target.value)}
                  />
                  <p className="text-xs text-blue-600 mt-1">Message played while dialing. Leave blank for silent mode.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <button onClick={handleGenerateScript} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50" disabled={loading}>
          {loading ? 'Generating...' : 'Generate Script'}
        </button>
      </div>

      {/* Right Panel */}
      <div className="flex-1 border border-gray-200 rounded-xl shadow p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-semibold mb-4">Generated Script</h2>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input
            type="text"
            className="flex-1 border rounded px-3 py-2 text-sm md:text-base"
            placeholder="Script Name"
            value={scriptName}
            onChange={(e) => setScriptName(e.target.value)}
          />
          <button
            onClick={handleSaveScript}
            className="bg-green-600 text-white px-3 md:px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 whitespace-nowrap text-sm md:text-base"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Script'}
          </button>
        </div>

        {/* Template suggestions */}
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-700 mb-2">Templates</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {TEMPLATES.map((t) => (
              <button
                key={t.name}
                onClick={() => {
                  setScriptName(t.name);
                  setScript(t.content);
                }}
                className="text-left border rounded px-3 py-2 hover:bg-blue-50 transition"
                title="Insert template into the editor"
              >
                <div className="font-medium text-gray-900">{t.name}</div>
                <div className="text-xs text-gray-500 line-clamp-2 mt-0.5">{t.content}</div>
              </button>
            ))}
          </div>
        </div>

        <textarea
          className="w-full border rounded px-3 py-2 text-xs md:text-sm font-mono min-h-[400px] md:min-h-[600px] resize-y"
          value={script}
          onChange={(e) => setScript(e.target.value)}
        />
      </div>
    </div>
  );
};

export default ScriptBuilder;
